import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination } from '@mui/material';
import isGHMC, { formatDateTime } from '../../../Common/helper/helpar';
import { useLocation, useNavigate } from 'react-router-dom';
import ExcelJS from "exceljs";

const LiveInnerReport = () => {
  // State for filters
  const [operationType, setOperationType] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const isProd = isGHMC();
  useEffect(() => {
    // console.log(location.state, "location.state");
    setRows(location.state);
  }, [location])

  // Filtered rows based on search
  const filteredRows = rows?.filter(row => {
    return (
      (operationType === '' || row.operationTypeName === operationType) &&
      (vehicleNumber === '' || row.vehicleNumber.toLowerCase().includes(vehicleNumber.toLowerCase()))
    );
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 100));
    setPage(0);
  };
  const duration = (time) => {
    const currentTime = new Date();
    const updatedTime = new Date(time);
    const durationMin = (currentTime - updatedTime) / (1000 * 60);
    return durationMin.toFixed(0) + " m";
  }

  const downloadAsExcel = async () => {
    // Create a new workbook and add a worksheet
    const dataToUse = rows;
    const currentDate = new Date();
    const formattedDate = `${currentDate.getDate()}-${currentDate.getMonth() + 1}-${currentDate.getFullYear()}`;
    const formattedTime = `${currentDate.getHours()}:${currentDate.getMinutes()}:${currentDate.getSeconds()}`;
    const downloadDateTime = `Traffic Marshal Live Inner Report Generated on: ${formattedDate}, ${formattedTime}`;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Live");

    // Define styles
    const boldFont = { bold: true };

    // Add the downloadDateTime to the correct row and columns, and ensure formatting
    worksheet.addRow(["", "", downloadDateTime, ""]);
    worksheet.mergeCells(1, 2, 1, 6); // Merge cells for downloadDateTime from column 2 to 6
    worksheet.getCell(1, 2).value = downloadDateTime;
    worksheet.getCell(1, 2).font = boldFont;
    worksheet.getCell(1, 2).alignment = { horizontal: "center" };

    // Apply styles to the merged cells (B1, C1, D1)
    for (let col = 2; col <= 6; col++) {
      worksheet.getCell(1, col).font = boldFont;
      worksheet.getCell(1, col).alignment = { horizontal: "center" };
    }

    // Add "Live Report" header
    worksheet.addRow(["", "", "Live Report", ""]);
    worksheet.mergeCells(2, 2, 2, 6); // Merge cells for Live Report from column 2 to 6
    worksheet.getCell(2, 2).value = "Live Inner Report";
    worksheet.getCell(2, 2).font = boldFont;
    worksheet.getCell(2, 2).alignment = { horizontal: "center" };

    // Apply styles to the merged cells (B2, C2, D2)
    for (let col = 2; col <= 6; col++) {
      worksheet.getCell(2, col).font = boldFont;
      worksheet.getCell(2, col).alignment = { horizontal: "center" };
    }

    // Add an empty row for spacing
    worksheet.addRow([""]);

    // Add table header row with bold font
    const tableHeaderRow = worksheet.addRow([
      "S.No",
      "Vehicle Number",
      "Equipment Type",
      "Operation Type",
      "In Date and Time	",
      "Duration (m)",
    ]);

    tableHeaderRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.font = boldFont;
      cell.alignment = { horizontal: "left" }; // Align text to the left
      if (colNumber >= 1 && colNumber <= 6) {
        // Apply background color and set text color
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "F6B1B1" }, // Background color for header
          bgColor: { argb: "FFFFFFFF" }, // White text color
        };
      }
    });

    // Add data rows
    dataToUse.forEach((item, index) => {
      worksheet
        .addRow([
          index + 1,
          item.vehicleNumber?? "N/A",
          item.vehicleTypeName?? "N/A",
          item.operationTypeName?? "N/A",
          formatDateTime(item.updatedDate)?? "N/A",
          duration(item.updatedDate)?? "N/A",
        ])
        .eachCell({ includeEmpty: true }, (cell, colNumber) => {
          if (colNumber >= 1 && colNumber <= 6) {
            // Align the columns from 1 to 6 to the left
            cell.alignment = { horizontal: "left" };
          } else {
            // Align the rest of the columns to the center
            cell.alignment = { horizontal: "center" };
          }
        });
    });

    // Set column widths dynamically based on content
    worksheet.columns = [
      { width: 5 },    // S.No
      { width: 20 },   // Vehicle Number
      { width: 30 },   // Equipment Type
      { width: 20 },   // Operation Type
      { width: 20 },   // In Date and Time
      { width: 20 },   // Duration (m)
    ];

    // Save the workbook to a file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "TrafficMarshalLiveInnerReport.xlsx"; // Filename for the Excel download
    a.click();
    URL.revokeObjectURL(url);  // Revoke the URL after download
  };

  const headerStyles = { textAlign: 'center', border: '1px solid #ddd', borderRight: 'none', padding: '10px', color: '#fff', backgroundColor: isProd ? "#004554" : "#EC1117", }
  const cellStyles = { textAlign: 'center', border: '1px solid #ddd', borderRight: 'none', padding: '10px', whiteSpace: "normal" };
  return (
    <div className="content">
      <div className="page-content">
        <h4 class="text-start mb-3">Live Inner Report</h4>
        <div className="card">
          <div className="card-body p-3">
            <div className="row align-items-center text-start">
              <div className="col-md-8">
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <input
                      placeholder="Search Vehicle Number"
                      type="text"
                      className="form-control bg-white"
                      value={vehicleNumber}
                      onChange={(e) => setVehicleNumber(e.target.value)}
                    />
                  </div>
                  {/* <div className="col-md-4">
                    <div className="form-group-filter">
                      <select
                        className="form-select bg-white"
                        value={operationType}
                        label="Operation Type"
                        onChange={(e) => setOperationType(e.target.value)}
                      >
                        <option value="">All Operation Type</option>
                        <option value="Weighbridge">Weighbridge</option>
                        <option value="Unloading">Unloading</option>
                        <option value="Fuel">Fuel</option>
                        <option value="Washing">Washing</option>
                      </select>
                    </div>
                  </div> */}
                  <div className="col-md-4">
                    <button 
                      onClick={() => { setOperationType(''); setVehicleNumber(''); }}
                      // className="btn btn-excel ms-0"
                      className={`btn ms-0 ${isProd ? "search-ghmc" : "search-swm"}`}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="d-flex justify-content-end">
                  <button className="btn btn-secondary  me-2" style={{}}
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </button>
                  <button 
                    className="btn btn-excel"
                    onClick={downloadAsExcel}
                  >
                    <i class="fa-solid fa-file-excel me-1 icon-container"></i> Excel
                  </button>
                </div>
              </div>
            </div>
            <div className='table-responsive'>
              <Paper sx={{ width: '100%', overflow: 'hidden', marginTop: '20px' }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    width: '100%',
                    maxHeight: '500px',
                    border: '1px solid #ccc',
                  }}
                >
                  <Table stickyHeader aria-label="sticky table">
                    <TableHead
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 0, // Ensure header stays above other content
                      }}
                    >
                      <TableRow sx={{ paddingTop: '20px' }}>
                        <TableCell sx={headerStyles}>S.No</TableCell>
                        <TableCell sx={headerStyles}>Vehicle Number</TableCell>
                        <TableCell sx={headerStyles}>Equipment Type</TableCell>
                        <TableCell sx={headerStyles}>Operation Type</TableCell>
                        <TableCell sx={headerStyles}>In Date and Time</TableCell>
                        <TableCell sx={headerStyles}>Duration (m)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRows?.length > 0 ? (
                        filteredRows?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((row, index) => (
                            <TableRow
                              key={index}
                              sx={{
                                backgroundColor: index % 2 === 1 ? 'rgba(0, 69, 84, 0.04)' : '#ffffff',
                                '&:nth-of-type(odd)': { backgroundColor: '#ffffff' },
                                '&:nth-of-type(even)': { backgroundColor: 'rgba(0, 69, 84, 0.04)' },
                              }}
                            >
                              <TableCell sx={cellStyles}>{row?.sNo}</TableCell>
                              <TableCell sx={cellStyles}>{row?.vehicleNumber}</TableCell>
                              <TableCell sx={cellStyles}>{row?.vehicleTypeName}</TableCell>
                              <TableCell sx={cellStyles}>{row?.operationTypeName}</TableCell>
                              <TableCell sx={cellStyles}>{formatDateTime(row?.updatedDate)}</TableCell>
                              <TableCell sx={cellStyles}>{duration(row.updatedDate)}</TableCell>
                            </TableRow>
                          ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={20} sx={{ textAlign: 'center' }}>No data available</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={rows?.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveInnerReport;
